import {
    DishFilter,
    DishKind,
    DishSort,
    EATERY_AUDIENCE,
    EATERY_DECOR,
    EATERY_EVENT,
    EATERY_GROUP,
    EateryAudience,
    EateryCard,
    EateryDecor,
    EateryDomain,
    EateryEvent,
    EateryFilter,
    EateryGroup,
    EateryInfo,
    EaterySortItem,
    EateryStats,
    Gene,
    is_eatery_group,
    SessionInfo,
    UserNotificationInfo,
} from 'api'
import mg, { FilterSpecification } from 'maplibre-gl'
import { UAParser } from 'ua-parser-js'
import { UiDuration, UiOpeninHours } from './opening_hours'
import {
    AUDIENCES_PRIORITY_DEF,
    DECORS_PRIORITY_DEF,
    EATERY_DOMAINS_PRIORITY_DEF,
    EATERY_GROUP_PRIORITY_DEF,
} from './defs'
import { addAlert, self } from 'store/user'

import jalaali from 'jalaali-js'
import { createMapFilter } from 'conf/eatery'
import { LOCALE } from 'locale'

export function toViewportNormalized(pos: Element): { x: number; y: number } {
    const clamp01 = (v: number) => Math.max(0, Math.min(1, v))

    if (
        typeof window === 'undefined' ||
        !window.innerWidth ||
        !window.innerHeight
    ) {
        return { x: 0.5, y: 0.5 }
    }

    let clientX: number | undefined
    let clientY: number | undefined

    const rect = pos.getBoundingClientRect()
    clientX = rect.left + rect.width / 2
    clientY = rect.top + rect.height / 2

    if (
        clientX == null ||
        clientY == null ||
        !isFinite(clientX) ||
        !isFinite(clientY)
    ) {
        return { x: 0.5, y: 0.5 }
    }

    const vw = window.innerWidth
    const vh = window.innerHeight

    const nx = clamp01(clientX / vw)
    const ny = clamp01(clientY / vh)

    return { x: nx, y: ny }
}

const { LngLat } = mg

export const IMAGE_MIMETYPE = [
    'image/png',
    'image/jpeg',
    'image/jpg',
    'image/gif',
    'image/webm',
    'image/webp',
]

export function is_dup(
    photos: File[],
    file: File,
    alert: boolean = true
): boolean {
    let found = photos.some(f => f.name == file.name && f.size == file.size)
    if (found && alert) {
        addAlert({
            type: 'error',
            timeout: 5,
            subject: 'عکس تکراری هستش',
        })
        return true
    }

    return false
}

export function clamp(value: number, min: number, max: number) {
    return Math.min(Math.max(value, min), max)
}

export function deepcopy<T>(value: T): T {
    return JSON.parse(JSON.stringify(value))
}

export function cloneDefault<T>(v: T): T {
    if (Array.isArray(v)) return v.map(cloneDefault) as T
    if (
        v &&
        typeof v === 'object' &&
        Object.getPrototypeOf(v) === Object.prototype
    ) {
        return Object.fromEntries(
            Object.entries(v).map(([k, x]) => [k, cloneDefault(x)])
        ) as T
    }
    return v // primitives, functions, class instances, Dates
}

export const addAnd = (
    filters: (EateryFilter | DishFilter | EaterySortItem | DishSort)[]
) => {
    filters.push({
        kind: 'and',
    })
}

// const parser = new UAParser(navigator.userAgent)

export let phoneRegex = /^(0|09|09[0-9]{1,9})$/

function record_gene_chars(gene: Gene): string {
    if (!gene || gene.length < 2) return ''

    return gene.slice(0, 2)
}

export function record_url(gene: Gene): string {
    if (!gene) return ''

    if (
        gene.startsWith('blob:') ||
        gene.startsWith('/record/') ||
        gene.startsWith('https:')
    )
        return gene

    const char = record_gene_chars(gene)

    if (!char) return ''

    return `/record/webp/${char}/${gene}`
}
export function video_url(gene: Gene): string {
    if (!gene) return ''

    if (
        gene.startsWith('blob:') ||
        gene.startsWith('/record/') ||
        gene.startsWith('https:')
    )
        return gene

    const char = record_gene_chars(gene)

    if (!char) return ''

    return `/record/mp4/${char}/${gene}`
}
export function video_url_m3u8(gene: Gene): string {
    if (!gene) return ''

    if (
        gene.startsWith('blob:') ||
        gene.startsWith('/record/') ||
        gene.startsWith('https:')
    )
        return gene

    const char = record_gene_chars(gene)

    if (!char) return ''

    return `/record/m3u8/${char}/${gene}/ostad.m3u8`
}

export function video_preview_url(gene: Gene): string {
    if (!gene) return ''

    if (
        gene.startsWith('blob:') ||
        gene.startsWith('/record/') ||
        gene.startsWith('https:')
    )
        return gene

    const char = record_gene_chars(gene)

    if (!char) return ''

    return `/record/trailer-preview/${char}/${gene}`
}

export const newUrl = (url: Gene): string => {
    if (!url) return ''
    if (url.startsWith('blob:')) return url

    const newU = url.concat(`?q=${performance.now()}`)

    return newU
}

export const persianPattern =
    /[\u0600-\u06FF\u0750-\u077F\u08A0-\u08FF\uFB50-\uFDFF\uFE70-\uFEFF]/u
export function extractPersianFromString(
    inputString: string,
    split: string
): string {
    const parts = inputString.split(split).map(part => part.trim())

    let result = []

    for (const part of parts) {
        if (persianPattern.test(part)) {
            result.push(part)
        }
    }

    if (result.length !== 0) return result.join(' ')

    return inputString
}

export function categoryDeciderTranslate(
    group: EateryGroup,
    domain: EateryDomain
): string {
    if (domain == 'none') return LOCALE.eatery_group(group)

    const sayBoth = is_eatery_group(domain)

    if (sayBoth) {
        if (domain == 'cafe') {
            return `${LOCALE.eatery_domain(domain)} ${LOCALE.eatery_group(group)}`
        }

        return `${LOCALE.eatery_group(group)} ${LOCALE.eatery_domain(domain)}`
    } else {
        return `${LOCALE.eatery_domain(domain)}`
    }
}
export function categoryDecider(group: EateryGroup, domain: EateryDomain) {
    return domain !== 'none' ? domain : group
}
export function themeDeciderTranslate(
    decor: EateryDecor,
    audience: EateryAudience
) {
    return decor !== 'none'
        ? LOCALE.eatery_decor(decor)
        : LOCALE.eatery_audience(audience)
}
export function themeDecider(decor: EateryDecor, audience: EateryAudience) {
    return decor !== 'none' ? decor : audience
}

export function shuffleInPlace<T>(arr: T[]): T[] {
    for (let i = arr.length - 1; i > 0; i--) {
        const j = (Math.random() * (i + 1)) | 0
        const tmp = arr[i] as T
        arr[i] = arr[j] as T
        arr[j] = tmp
    }
    return arr
}

export const cubicBezier = (
    t: number,
    p1: number,
    p2: number,
    p3: number,
    p4: number
) => {
    p3
    return (
        3 * p1 * (1 - t) * (1 - t) * t +
        3 * p2 * (1 - t) * t * t +
        p4 * t * t * t
    )
}

export type getDistanceType = {
    value: number
    unit: 'متر' | 'کیلومتر'
}
export function getDistance(
    lat1: number,
    lng1: number,
    lat2: number,
    lng2: number,
    format: boolean = false,
    minKm: number = 0.1
): getDistanceType {
    const eatery = new LngLat(lat1, lng1)
    const user = new LngLat(lat2, lng2)
    const distance = Math.floor(user.distanceTo(eatery))

    if (format) {
        return distance > 1000
            ? { value: Math.floor(distance / 1000), unit: 'کیلومتر' }
            : { value: distance, unit: 'متر' }
    }

    const km = Math.max(minKm, Math.floor((distance / 1000) * 10) / 10)

    return {
        unit: 'کیلومتر',
        value: km,
    }
}
export function estimateDriveTimeFromKm(distanceKm: number) {
    const CITY_SPEED_KMPH = 10 // سرعت میانگین درون‌شهری
    const HIGHWAY_SPEED_KMPH = 40 // برای مسافت‌های بلندتر
    const LONG_DISTANCE_THRESHOLD_KM = 20 // اگر بیشتر از این فرض می‌کنیم بزرگراه

    const speed =
        distanceKm > LONG_DISTANCE_THRESHOLD_KM
            ? HIGHWAY_SPEED_KMPH
            : CITY_SPEED_KMPH

    // دقیقه = فاصله (km) / سرعت (km/h) * 60
    let minutes = Math.ceil((distanceKm / speed) * 60)

    // جلوگیری از نمایش زمان غیرواقعی برای خیلی نزدیک‌ها
    if (minutes < 3) minutes = 3

    const hours = Math.floor(minutes / 60)
    const remMinutes = minutes % 60

    const text =
        hours > 0
            ? remMinutes > 0
                ? `${hours} ساعت ${remMinutes} دقیقه`
                : `${hours} ساعت`
            : `${minutes} دقیقه`

    return { minutes, text }
}

export const OS = ['unknown', 'linux', 'windows', 'mac', 'ios', 'android']
export const BROWSERS = [
    'unknown',
    'chrome',
    'firefox',
    'safari',
    'opera',
    'edge',
]
export const DEVICES = ['unknown', 'desktop', 'mobile', 'embeded']

export function convert_version(version: string | number) {
    if (!version) return 0

    if (typeof version == 'string') {
        version = parseInt(version.split('.')[0]!) || 1
    }

    return Math.max(Math.min(version, 1 << 15), 1)
}

export function get_session_info(): SessionInfo {
    const parser = new UAParser(navigator.userAgent)
    let { os, device, browser } = parser.getResult()

    let osn = (os.name || '').toLowerCase()
    let brn = (browser.name || '').toLowerCase()
    let dvn = (device.type || '').toLowerCase()

    return {
        client: 2,
        os: Math.max(OS.indexOf(osn), 0),
        browser: Math.max(BROWSERS.indexOf(brn), 0),
        device: Math.max(DEVICES.indexOf(dvn), 0),
        os_version: convert_version(os.version!),
        client_version: 1,
        browser_version: convert_version(browser.version!),
    }
}

export function formatHourFa(hour24: number): string {
    if (hour24 < 0 || hour24 > 24) return ''

    // normalize 24 → 0
    const h = hour24 === 24 ? 0 : hour24

    let label: 'صبح' | 'ظهر' | 'عصر' | 'شب'

    if (h < 12) label = 'صبح'
    else if (h < 16) label = 'ظهر'
    else if (h <= 18) label = 'عصر'
    else label = 'شب'

    // convert to 12-hour format
    const hour12 = h === 0 ? 12 : h > 12 ? h - 12 : h

    return `${hour12} ${label}`
}

export const format15MinuteFa = (time1: number): string => {
    const time = time1 ?? 0

    const totalMinutes = time * 15 // same approach as formatOhTime
    const hours = Math.floor(totalMinutes / 60) // 0..24 possible when totalMinutes==1440
    const minutes = totalMinutes % 60

    // normalize 24 -> 0 for labeling consistency
    const h = hours === 24 ? 0 : hours

    if (hours === 24) {
        return `12:00 شب`
    }

    // pick Persian label
    // intervals: 0-11 -> صبح, 12-15 -> ظهر, 16-20 -> عصر, 21-23 -> شب
    let label: 'صبح' | 'ظهر' | 'عصر' | 'شب'
    if (h < 12) label = 'صبح'
    else if (h < 16) label = 'ظهر'
    else if (h < 21) label = 'عصر'
    else label = 'شب'

    // convert to 12-hour display (0 => 12)
    const hour12 = h === 0 ? 12 : h > 12 ? h - 12 : h

    const mmStr = String(minutes).padStart(2, '0')
    return `${hour12}:${mmStr} ${label}`
}

export const formatOhTime = (time: number): string | null => {
    if (time < 0) return null

    const totalMinutes = time * 15
    const hours = Math.floor(totalMinutes / 60)
    const minutes = totalMinutes % 60
    return `${String(hours)}:${String(minutes).padStart(2, '0')}`
}

export type starsType = [number, number, number, number, number]
export function getAverageRating(stars: starsType): number {
    const total = stars.reduce((s, c) => s + c, 0)
    if (total === 0) return 0

    // numerator = 1*counts[0] + 2*counts[1] + ... + 5*counts[4]
    const numerator = stars.reduce((sum, c, i) => sum + (i + 1) * c, 0)
    return numerator / total
}

export function isIOS() {
    if (typeof navigator === 'undefined') return false

    const ua = navigator.userAgent || navigator.vendor
    return /iphone|ipad|ipod/i.test(ua)
}

export const isTWA = () => {
    return !!globalThis.GOOJE_BRIDGE
}

export function isAndroidOrOther() {
    if (typeof navigator === 'undefined') return false

    const ua = navigator.userAgent || navigator.vendor
    const isAndroidOrMobileUA = /android|opera mini|iemobile|mobile/i.test(ua)
    const isSmallScreen = window.matchMedia('(max-width: 768px)').matches

    return isAndroidOrMobileUA || isSmallScreen
}

export function isMobile() {
    return isIOS() || isAndroidOrOther()
}

export const smoothScrollTo = (
    element: HTMLElement,
    targetScrollLeft: number,
    duration: number
) => {
    const start = element.scrollLeft
    const distance = targetScrollLeft - start
    const startTime = performance.now()

    const easing = (t: number) => cubicBezier(t, 0, 0.25, 0.5, 1)

    const step = (currentTime: number) => {
        const timeElapsed = currentTime - startTime
        const progress = Math.min(timeElapsed / duration, 1) // Cap progress at 1 (100%)

        // Calculate new scroll position with easing function
        element.scrollLeft = start + distance * easing(progress)

        // Continue scrolling if the progress is less than 1
        if (progress < 1) {
            requestAnimationFrame(step)
        }
    }

    // Start the animation
    requestAnimationFrame(step)
}

export function isOpenDuringHour(
    openingHours: UiOpeninHours,
    dayIndex: number,
    hour: number
) {
    const SLOTS_PER_DAY = 24 * 4
    const hourStartSlot = (h: number) => h * 4
    const hourEndSlot = (h: number) => (h + 1) * 4

    const start = hourStartSlot(hour)
    const end = hourEndSlot(hour)

    // check intervals of the dayIndex
    const intervals: any[] = openingHours[dayIndex] ?? []

    for (const it of intervals) {
        if (!it) continue
        // 24 / 7
        if (it.open === 1 && it.close === 1) return true

        if (it.open === 0 && it.close === 0) continue

        const o = it.open
        const c = it.close

        if (o < c) {
            // same-day interval — overlap if o < end && c > start
            if (o < end && c > start) return true
        } else if (o > c) {
            // overnight interval that starts this day and closes next day
            // this interval covers [o, 96) on this day
            if (o < end && SLOTS_PER_DAY > start) {
                // overlap with [o,96)
                if (o < end && SLOTS_PER_DAY > start) return true
            }
            // and it covers [0, c) on the next day — handled when checking next day
        }
    }

    // Additionally check previous day's overnight intervals that wrap into this day
    const prevDay = (dayIndex + 6) % 7
    const prevIntervals: any[] = openingHours[prevDay] ?? []
    for (const it of prevIntervals) {
        if (!it) continue
        if (it.open === 1 && it.close === 1) return true
        if (it.open > it.close) {
            // prev day interval covers [0, c) on this day
            const c = it.close
            // overlap if 0 < end && c > start  -> i.e., c > start
            if (c > start) return true
        }
    }

    return false
}

export function isOpenNow(openingHours: UiOpeninHours): boolean {
    const now = new Date()
    const jsDay = now.getDay()
    const currentDay = (jsDay + 1) % 7
    const currentHour = now.getHours()
    const currentMinutes = now.getMinutes()

    const currentSlot = Math.floor(currentHour * 4 + currentMinutes / 15)

    const isInsideInterval = (slot: number, interval: UiDuration): boolean => {
        if (!interval) return false

        if (interval.open === 1 && interval.close === 1) return true

        // normal interval: 24 -> 40
        if (interval.open < interval.close) {
            return interval.open <= slot && slot < interval.close
        }

        // wrapped interval: 24 -> 2
        if (interval.open > interval.close) {
            return slot >= interval.open || slot < interval.close
        }

        return false
    }

    const todayIntervals = openingHours[currentDay] ?? []
    for (const interval of todayIntervals) {
        if (isInsideInterval(currentSlot, interval)) {
            return true
        }
    }

    const prevDay = (currentDay + 6) % 7
    const prevIntervals = openingHours[prevDay] ?? []
    for (const interval of prevIntervals) {
        if (!interval) continue

        // only wrapped intervals can continue into today's early hours
        if (interval.open > interval.close) {
            if (currentSlot < interval.close) {
                return true
            }
        }

        if (interval.open === 1 && interval.close === 1) {
            return true
        }
    }

    return false
}

export function convertPersianPriceToNumber(str: string): string {
    return (
        str
            // keep only ASCII digits + both Eastern ranges
            .replace(/[^\d\u06F0-\u06F9\u0660-\u0669]/g, '')
            // Persian digits → ASCII
            .replace(/[\u06F0-\u06F9]/g, d => String(d.charCodeAt(0) - 0x06f0))
            // Arabic-Indic digits → ASCII
            .replace(/[\u0660-\u0669]/g, d => String(d.charCodeAt(0) - 0x0660))
    )
}
export const convertPersianDigitsToEnglish = (str: string): string => {
    return str
        .replace(/[\u06F0-\u06F9]/g, d => String(d.charCodeAt(0) - 0x06f0))
        .replace(/[\u0660-\u0669]/g, d => String(d.charCodeAt(0) - 0x0660))
}
export const removeNumbersAndSymbols = (str: string): string => {
    return str.replace(/[^\p{L}\p{M}\s]/gu, '')
}

const HEXCHARS = '0123456789abcdefABCDEF'
export function isGene(value: any): value is Gene {
    if (typeof value != 'string') return false
    if (value.length != 32) return false

    let gene: Gene = value.toLowerCase()

    for (let c of gene) {
        if (!HEXCHARS.includes(c)) return false
    }

    return true
}

export function getOpenUntil(
    openingHours: UiOpeninHours,
    format: boolean = false
): string {
    const now = new Date()
    const currentDay = (now.getDay() + 1) % 7
    const currentHour = now.getHours()
    const currentMinutes = now.getMinutes()
    const currentSlot = currentHour * 4 + Math.floor(currentMinutes / 15)

    const todaysHours = openingHours[currentDay] ?? []

    const isActivePeriod = (period: UiDuration, slot: number): boolean => {
        if (!period) return false

        const { open, close } = period

        // same-day interval: 8..20
        if (open < close) {
            return open <= slot && slot < close
        }

        // wrapped interval: 24..2
        if (open > close) {
            return slot >= open || slot < close
        }

        return false
    }

    const isTwentyFourHours = (period: UiDuration): boolean => {
        return !!period && period.open === 0 && period.close === 96
    }

    // 1) Check today's intervals
    let currentPeriod = todaysHours.find(period =>
        isActivePeriod(period, currentSlot)
    )

    // 2) Also check yesterday's wrapped intervals that continue into today
    if (!currentPeriod) {
        const prevDay = (currentDay + 6) % 7
        const prevHours = openingHours[prevDay] ?? []

        currentPeriod = prevHours.find(period => {
            if (!period) return false
            // only wrapped intervals can spill into today
            return period.open > period.close && currentSlot < period.close
        })
    }

    if (!currentPeriod) return '-- : --'

    if (isTwentyFourHours(currentPeriod)) {
        return '24 ساعته بازه'
    }

    return format
        ? (format15MinuteFa(currentPeriod.close) ?? '-- : --')
        : (formatOhTime(currentPeriod.close) ?? '-- : --')
}

export function suggestDrinks(time = new Date(), count: number) {
    const date = time instanceof Date ? time : new Date(time)
    const h = date.getHours()

    let pool: DishKind[]
    if (h >= 5 && h <= 13) {
        // breakfast
        pool = [
            'tea',
            'tea_herbal',
            'tea_ice',
            'coffee',
            'coffee_ice',
            'chocolate',
        ]
    } else if (h > 13 && h <= 15) {
        // lunch
        pool = ['juice', 'milkshake', 'smoothie', 'chocolate', 'coffee', 'tea']
    } else if (h > 15 && h <= 18) {
        // afternoon snack
        pool = [
            'juice',
            'milkshake',
            'smoothie',
            'chocolate',
            'coffee_ice',
            'tea_ice',
        ]
    } else if (h > 18 && h <= 22) {
        // dinner
        pool = [
            'juice',
            'milkshake',
            'smoothie',
            'chocolate',
            'coffee',
            'coffee_ice',
            'tea_herbal',
            'tea',
            'tea_ice',
        ]
    } else if (h > 22 && h <= 23) {
        // late‐evening
        pool = [
            'juice',
            'milkshake',
            'smoothie',
            'chocolate',
            'coffee',
            'coffee_ice',
            'tea_herbal',
            'tea',
            'tea_ice',
        ]
    } else {
        pool = [
            'tea',
            'tea_herbal',
            'tea_ice',
            'coffee',
            'coffee_ice',
            'chocolate',
        ]
    }

    const pick = pool.slice()
    for (let i = pick.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1))
        ;[pick[i], pick[j]] = [pick[j]!, pick[i]!]
    }

    return pick.slice(0, count)
}
export function suggestFoods(time = new Date(), count: number) {
    const date = time instanceof Date ? time : new Date(time)
    const h = date.getHours()

    let pool: DishKind[]
    if (h >= 5 && h < 12) {
        // breakfast
        pool = [
            'friedegg',
            'omelette',
            'croissant',
            'cake',
            'cookie',
            'milkshake',
            'sandwich',
            'waffle',
        ]
    } else if (h >= 12 && h <= 15) {
        // lunch
        pool = [
            'polo',
            'meat',
            'kabab',
            'chicken',
            'sandwich',
            'pasta',
            'seafood',
            'noodle',
        ]
    } else if (h > 15 && h <= 18) {
        // afternoon snack
        pool = [
            'burger',
            'sandwich',
            'chicken',
            'cookie',
            'cake',
            'waffle',
            'icecream',
            'noodle',
        ]
    } else if (h > 18 && h <= 22) {
        // dinner
        pool = [
            'fried',
            'steak',
            'seafood',
            'pasta',
            'kabab',
            'chicken',
            'soup',
            'pizza',
            'burger',
        ]
    } else if (h > 22 && h <= 23) {
        // late‐evening
        pool = [
            'ash',
            'pizza',
            'burger',
            'sandwich',
            'sandwich_hotdog',
            'fried',
        ]
    } else {
        pool = [
            'ash',
            'pizza',
            'burger',
            'sandwich',
            'sandwich_hotdog',
            'chicken',
            'fried',
        ]
    }

    const pick = pool.slice()
    for (let i = pick.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1))
        ;[pick[i], pick[j]] = [pick[j]!, pick[i]!]
    }

    return pick.slice(0, count)
}

export const JalaliToDate = (
    day: number,
    month: number,
    year: number
): number | null => {
    if (
        !Number.isFinite(day) ||
        !Number.isFinite(month) ||
        !Number.isFinite(year)
    ) {
        return null
    }

    const jy = Math.floor(year)
    const jm = Math.floor(month)
    const jd = Math.floor(day)

    if (jy < 1200) {
        return null
    }

    if (jm < 1 || jm > 12) return null

    const maxDay = jalaali.jalaaliMonthLength(jy, jm)
    if (jd < 1 || jd > maxDay) return null

    const { gy, gm, gd } = jalaali.toGregorian(jy, jm, jd)

    const utcMs = Date.UTC(gy, gm - 1, gd)
    if (!Number.isFinite(utcMs)) return null

    return Math.floor(utcMs / 1000)
}

export function daysUntil(birthTs: number) {
    const MS_PER_DAY = 24 * 60 * 60 * 1000

    // normalize input -> milliseconds (accepts seconds or ms)
    const birthMs = birthTs * 1e3
    const nowMs = Date.now()

    // --- use LOCAL date components (not UTC) ---
    const birthG = new Date(birthMs)
    const { jm: birthJm, jd: birthJd } = jalaali.toJalaali(
        birthG.getFullYear(),
        birthG.getMonth() + 1, // local month (1-12)
        birthG.getDate()
    )

    const nowG = new Date(nowMs)
    const { jy: nowJy } = jalaali.toJalaali(
        nowG.getFullYear(),
        nowG.getMonth() + 1,
        nowG.getDate()
    )

    const jToLocalMidnightMs = (jy: number, jm: number, jd: number) => {
        const { gy, gm, gd } = jalaali.toGregorian(jy, jm, jd)
        // create a Date at local midnight for that gregorian date
        return new Date(gy, gm - 1, gd).setHours(0, 0, 0, 0)
    }

    let candJy = nowJy
    let candLocalMs = jToLocalMidnightMs(candJy, birthJm, birthJd)

    // local midnight for "today"
    const nowLocalMidnightMs = new Date(
        nowG.getFullYear(),
        nowG.getMonth(),
        nowG.getDate()
    ).setHours(0, 0, 0, 0)

    if (candLocalMs < nowLocalMidnightMs) {
        candJy += 1
        candLocalMs = jToLocalMidnightMs(candJy, birthJm, birthJd)
    }

    // difference should be an exact multiple of MS_PER_DAY (midnights),
    // so rounding is safe.
    const days = Math.round((candLocalMs - nowLocalMidnightMs) / MS_PER_DAY)

    const text =
        days === 0 ? 'امروزه!' : days === 1 ? 'فردا!' : `${days} روز مونده!`

    return { days, text, next: Math.floor(candLocalMs / 1000) }
}

const timeagoUnits: { limit: number; divisor: number; name: string }[] = [
    { limit: 60, divisor: 1, name: 'ثانیه' },
    { limit: 3600, divisor: 60, name: 'دقیقه' },
    { limit: 86400, divisor: 3600, name: 'ساعت' },
    { limit: 2592000, divisor: 86400, name: 'روز' },
    { limit: 31536000, divisor: 2592000, name: 'ماه' },
    { limit: Infinity, divisor: 31536000, name: 'سال' },
]

export function timeAgo(timestamp: Date | number): string {
    const now = new Date()
    const then = typeof timestamp === 'number' ? new Date(timestamp) : timestamp
    const diffMs = Math.max(0, now.getTime() - then.getTime())
    const diffSec = Math.floor(diffMs / 1000)

    if (diffMs > 0) {
        for (const { limit, divisor, name } of timeagoUnits) {
            if (diffSec < limit) {
                const value = Math.floor(diffSec / divisor)
                return `${value} ${name} قبل`
            }
        }
    }

    return 'چند ثانیه پیش'
}
export function nullableTimeAgo(timestamp: Date | number): string | null {
    const now = new Date()
    const then = typeof timestamp === 'number' ? new Date(timestamp) : timestamp
    const diffMs = now.getTime() - then.getTime()
    const diffSec = Math.floor(diffMs / 1000)

    for (const { limit, divisor, name } of timeagoUnits) {
        if (diffSec < limit) {
            const value = Math.max(Math.floor(diffSec / divisor), 0)

            if (value != 0) {
                return `${value} ${name} قبل`
            }
        }
    }

    // Fallback (shouldn't reach here)
    return null
}

export function daysAgo(timestamp: Date | number): number {
    const now = new Date()
    const then = typeof timestamp === 'number' ? new Date(timestamp) : timestamp
    const diffMs = Math.max(0, now.getTime() - then.getTime())
    const diffSec = Math.floor(diffMs / 1000)

    if (diffMs > 0) {
        const value = Math.floor(diffSec / 86400)
        return value
    }

    return 0
}

const timeleftUnits: { limit: number; divisor: number; name: string }[] = [
    { limit: 60, divisor: 1, name: 'ثانیه' },
    { limit: 3600, divisor: 60, name: 'دقیقه' },
    { limit: 86400, divisor: 3600, name: 'ساعت' },
    { limit: 2592000, divisor: 86400, name: 'روز' },
    { limit: 31536000, divisor: 2592000, name: 'ماه' },
    { limit: Infinity, divisor: 31536000, name: 'سال' },
]
export function timeLeft(timestamp: Date | number): string {
    const now = new Date()
    const then = typeof timestamp === 'number' ? new Date(timestamp) : timestamp

    const diffMs = then.getTime() - now.getTime()
    const diffSec = Math.floor(diffMs / 1000)

    if (diffSec <= 0) return 'تمام شده'

    for (const { limit, divisor, name } of timeleftUnits) {
        if (diffSec < limit) {
            const value = Math.max(Math.floor(diffSec / divisor), 0)
            return `${value} ${name}`
        }
    }

    return 'چند ثانیه'
}

export function throttle<T extends (...args: any[]) => void>(
    fn: T,
    delay: number
): T {
    let lastCall = 0
    return ((...args: any[]) => {
        const now = Date.now()
        if (now - lastCall >= delay) {
            lastCall = now
            fn(...args)
        }
    }) as T
}

export function debounce<T extends (...args: any[]) => void>(
    fn: T,
    delay: number
): T {
    let timeoutId: ReturnType<typeof setTimeout> | null = null

    return ((...args: any[]) => {
        if (timeoutId !== null) {
            clearTimeout(timeoutId)
        }
        timeoutId = setTimeout(() => {
            fn(...args)
        }, delay)
    }) as T
}

export const touchPosition = (event: any) =>
    event.touches ? event.touches[0] : event

/**
 * Smoothly scrolls the container to the target scrollTop and calls onFinish when scrolling is done.
 *
 * @param container - The element to scroll.
 * @param options - The target vertical scroll position.
 * @param onFinish - Callback to run when scrolling has finished.
 * @param throttle - Optional settings (e.g., delay in ms).
 */
export function scrollWithCallback(
    container: HTMLElement,
    options: ScrollToOptions,
    onFinish: () => void,
    throttle?: number
) {
    const t = throttle ?? 100
    let scrollFinishTimer: number
    const ac = new AbortController()

    const onScroll = () => {
        if (scrollFinishTimer) clearTimeout(scrollFinishTimer)
        scrollFinishTimer = window.setTimeout(() => {
            // Remove the scroll listener and abort the signal
            container.removeEventListener('scroll', onScroll)
            ac.abort()
            onFinish()
        }, t)
    }

    container.addEventListener('scroll', onScroll, {
        passive: true,
        signal: ac.signal,
    })

    container.scrollTo(options)
}

export const isEnglish = (text: string) =>
    /^[A-Za-z0-9\s.,!?'"():;/-]*$/.test(text.trim())

// map tools
export function addGeneFilterToLayer(
    map: maplibregl.Map,
    layerId: string,
    geneToRemove: string
) {
    if (!map || !map.getLayer(layerId)) return

    let existing = map.getFilter(layerId) as any[] | null

    const geneFilter: FilterSpecification = ['!=', 'gene', geneToRemove]

    let newFilter: any[]

    if (!existing) {
        newFilter = ['all', geneFilter]
    } else if (existing[0] !== 'all') {
        newFilter = ['all', existing, geneFilter]
    } else {
        newFilter = [...existing, geneFilter]
    }

    // 3. Apply it
    map.setFilter(layerId, newFilter as FilterSpecification)
}
export function removeGeneFilterFromLayer(
    map: maplibregl.Map,
    layerId: string,
    geneToRemove: string
) {
    if (!map || !map.getLayer(layerId)) return

    // 1. Grab the existing filter
    const existing = map.getFilter(layerId) as any[] | null
    if (!existing) {
        // nothing to remove
        return
    }

    let newFilter: any[] | null

    if (existing[0] === 'all') {
        // 2. Filter out any ['!=','gene', geneToRemove] clauses
        const clauses = existing
            .slice(1)
            .filter(
                clause =>
                    !(
                        Array.isArray(clause) &&
                        clause[0] === '!=' &&
                        clause[1] === 'gene' &&
                        clause[2] === geneToRemove
                    )
            )

        // 3a. If nothing remains, clear the filter
        if (clauses.length === 0) {
            newFilter = null
        }
        // 3b. If only one clause remains, use it directly
        else if (clauses.length === 1) {
            newFilter = clauses[0]
        }
        // 3c. Otherwise re‑wrap in ['all', ...]
        else {
            newFilter = ['all', ...clauses]
        }
    } else {
        // existing is a single-expression filter—not wrapped in 'all'
        // only clear it if it exactly matches our gene filter
        const isGeneOnly =
            existing[0] === '!=' &&
            existing[1] === 'gene' &&
            existing[2] === geneToRemove

        newFilter = isGeneOnly ? null : existing
    }

    // 4. Apply the cleaned-up filter (or remove it)
    if (newFilter) {
        map.setFilter(layerId, newFilter as FilterSpecification)
    } else {
        // remove any custom filter—reverts to showing all features
        map.setFilter(layerId, null)
    }
}

type MapLayerFilter =
    | {
          kind: 'event'
          value: EateryEvent
      }
    | {
          kind: 'decor'
          value: EateryDecor
      }
    | {
          kind: 'audience'
          value: EateryAudience
      }
    | {
          kind: 'domain'
          value: EateryDomain
      }
    | {
          kind: 'group'
          value: EateryGroup
      }
export function setMapLayerFilter(
    map: maplibregl.Map,
    layerId: string,
    filter: MapLayerFilter
) {
    if (!map.getLayer(layerId)) return

    switch (filter.kind) {
        case 'event': {
            if (filter.value === 'none') {
                map.setFilter(layerId, createMapFilter())
                return
            }

            map.setFilter(
                layerId,
                createMapFilter([
                    '==',
                    'eevent',
                    EATERY_EVENT.indexOf(filter.value),
                ])
            )

            return
        }

        case 'decor': {
            if (filter.value === 'none') {
                map.setFilter(layerId, createMapFilter())
                return
            }

            map.setFilter(
                layerId,
                createMapFilter([
                    '==',
                    'decor',
                    EATERY_DECOR.indexOf(filter.value),
                ])
            )

            return
        }

        case 'audience': {
            if (filter.value === 'none') {
                map.setFilter(layerId, createMapFilter())
                return
            }

            map.setFilter(
                layerId,
                createMapFilter([
                    '==',
                    'audience',
                    EATERY_AUDIENCE.indexOf(filter.value),
                ])
            )

            return
        }

        case 'domain': {
            if (filter.value === 'none') {
                map.setFilter(layerId, createMapFilter())
                return
            }

            map.setFilter(
                layerId,
                createMapFilter(['==', 'domain', filter.value])
            )

            return
        }

        case 'group': {
            if (filter.value === 'unknown') {
                map.setFilter(layerId, createMapFilter())
                return
            }

            map.setFilter(
                layerId,
                createMapFilter([
                    'any',
                    ['==', 'group', EATERY_GROUP.indexOf(filter.value)],
                    // ['==', 'domain', filter.value],
                ])
            )

            return
        }
    }
}

export function hideLayer(map: maplibregl.Map, ...layerId: string[]) {
    if (!map) return
    for (let lid of layerId) {
        if (map.getLayer(lid)) {
            map.setLayoutProperty(lid, 'visibility', 'none')
        }
    }
}
export function showLayer(map: maplibregl.Map, ...layerId: string[]) {
    if (!map) return
    for (let lid of layerId) {
        if (map.getLayer(lid)) {
            map.setLayoutProperty(lid, 'visibility', 'visible')
        }
    }
}

export function openNavigationApp(lat: number, lng: number): void {
    const destination = `${lat},${lng}`
    const encodedDest = encodeURIComponent(destination)

    const googleMapsWeb = `https://www.google.com/maps/dir/?api=1&destination=${encodedDest}&travelmode=driving`
    const appleMapsWeb = `https://maps.apple.com/?daddr=${encodedDest}&dirflg=d`

    const googleMapsDeepIOS = `comgooglemaps://?daddr=${encodedDest}&directionsmode=driving` // iOS Google Maps
    const geoFallback = `geo:${destination}?q=${encodedDest}`

    // Not mobile -> open web directions in new tab
    if (!isMobile()) {
        window.open(googleMapsWeb, '_blank')
        return
    }

    // Mobile behavior
    if (isIOS()) {
        // Try Google Maps app first (if installed) then Apple Maps then web
        try {
            // try google maps deep link first
            window.location.href = googleMapsDeepIOS
        } catch (e) {
            window.location.assign(appleMapsWeb)
        }
        return
    }

    if (isAndroidOrOther()) {
        try {
            window.location.assign(geoFallback)
        } catch (e) {
            window.open(googleMapsWeb, '_blank')
        }
        return
    }

    // Fallback for other mobile devices: open google web directions
    window.open(googleMapsWeb, '_blank')
}

export const default_confetties = () => {
    if (
        typeof window === 'undefined' ||
        // @ts-ignore
        typeof window.confetti !== 'function'
    )
        return

    const count = 200,
        defaults = {
            origin: { y: 0.7 },
        }

    function fire(particleRatio: number, opts: any) {
        // @ts-ignore
        window.confetti(
            Object.assign({}, defaults, opts, {
                particleCount: Math.floor(count * particleRatio),
            })
        )
    }

    fire(0.25, {
        spread: 26,
        startVelocity: 55,
    })

    fire(0.2, {
        spread: 60,
    })

    fire(0.35, {
        spread: 100,
        decay: 0.91,
        scalar: 0.8,
    })

    fire(0.1, {
        spread: 120,
        startVelocity: 25,
        decay: 0.92,
        scalar: 1.2,
    })

    fire(0.1, {
        spread: 120,
        startVelocity: 45,
    })
}

export const normalizeInput = (v: string): string => {
    return convertPersianDigitsToEnglish(v)
        .replaceAll('ي', 'ی')
        .replaceAll('ى', 'ی')
        .replaceAll('آ', 'ا')
        .replaceAll('أ', 'ا')
        .replaceAll('إ', 'ا')
        .replaceAll('ٱ', 'ا')
        .replaceAll('ؤ', 'و')
        .replaceAll('ئ', 'ی')
        .replaceAll('ك', 'ک')
        .replace(/\u200C/g, ' ')
        .replace(/\s+/g, ' ')
}

export function EateryInfoToEateryCard(e: EateryInfo): EateryCard {
    const eatery = deepcopy(e)

    const photo = eatery.photos.find(Boolean) || ''

    return {
        ...eatery,
        photo,
    }
}

export function getUnreadNotifsCount(list: UserNotificationInfo[]): number {
    let total = 0

    for (let i = 0; i < list.length; i++) {
        const notif = list[i]

        if (notif && !notif.read_at) total += 1
    }

    return total
}

export const waitUntilTime = async (startedAt: number, minimumMs: number) => {
    const elapsed = Date.now() - startedAt

    if (elapsed >= minimumMs) return

    await new Promise(resolve => setTimeout(resolve, minimumMs - elapsed))
}

export const goojeBaseUrl = (path?: string): string => {
    if (typeof window === 'undefined') return path ?? ''

    const u = new URL(path ?? '', window.location.origin)

    const isLocal =
        u.hostname === 'localhost' ||
        u.hostname === '127.0.0.1' ||
        u.hostname.endsWith('.local')

    if (isLocal) {
        u.protocol = 'https:'
        u.hostname = 'sky.gooje.app'
        u.port = ''
    }

    return u.toString()
}

export const discountExpired = (expireAt: number): boolean => {
    if (!expireAt) return true

    return Math.floor(Date.now() / 1e3) > expireAt
}

//
type PRIORITY_KEY = EateryAudience | EateryDecor | EateryDomain | EateryGroup

const rankedPriorityValues = <T extends PRIORITY_KEY>(
    counts: Partial<Record<T, number>> | undefined,
    defaults: readonly T[]
): T[] => {
    if (!counts) return []

    const defaultIndex = new Map(defaults.map((value, index) => [value, index]))

    return (Object.entries(counts) as [T, number][])
        .filter(
            ([key, count]) =>
                defaultIndex.has(key) && Number.isFinite(count) && count > 0
        )
        .sort(
            ([a, countA], [b, countB]) =>
                countB - countA ||
                (defaultIndex.get(a) ?? Infinity) -
                    (defaultIndex.get(b) ?? Infinity)
        )
        .map(([key]) => key)
}

const resolveUserPriority = <T extends PRIORITY_KEY>(
    history: Partial<Record<T, number>> | undefined,
    preference: Partial<Record<T, number>> | undefined,
    defaults: readonly T[]
): T[] => {
    return [
        ...new Set([
            ...rankedPriorityValues(history, defaults),
            ...rankedPriorityValues(preference, defaults),
            ...defaults,
        ]),
    ]
}

let userDecorsPriorityCalced: EateryDecor[] | null = null
export const userDecorsPriority = (): EateryDecor[] => {
    if (userDecorsPriorityCalced) return userDecorsPriorityCalced

    const history = self.user?.history_eatery_view?.recently?.decor
    const preference = self.preferences?.decor

    userDecorsPriorityCalced = resolveUserPriority(
        history,
        preference,
        DECORS_PRIORITY_DEF
    ).filter(decor => decor !== 'none' && !decor.startsWith('___'))

    return userDecorsPriorityCalced
}

let userGroupsPriorityCalced: EateryGroup[] | null = null
export const userGroupsPriority = (): EateryGroup[] => {
    if (userGroupsPriorityCalced) return userGroupsPriorityCalced

    const history = self.user?.history_eatery_view?.recently?.group
    const preference = self.preferences?.group

    userGroupsPriorityCalced = resolveUserPriority(
        history,
        preference,
        EATERY_GROUP_PRIORITY_DEF
    ).filter(group => group !== 'unknown' && !group.startsWith('___'))

    return userGroupsPriorityCalced
}

let userDomainsPriorityCalced: EateryDomain[] | null = null
export const userDomainsPriority = (): EateryDomain[] => {
    if (userDomainsPriorityCalced) return userDomainsPriorityCalced

    const history = self.user?.history_eatery_view?.recently?.domain
    const preference = self.preferences?.domain

    userDomainsPriorityCalced = resolveUserPriority(
        history,
        preference,
        EATERY_DOMAINS_PRIORITY_DEF
    ).filter(domain => domain !== 'none' && !domain.startsWith('___'))

    return userDomainsPriorityCalced
}

let userAudiencesPriorityCalced: EateryAudience[] | null = null
export const userAudiencesPriority = (): EateryAudience[] => {
    if (userAudiencesPriorityCalced) return userAudiencesPriorityCalced

    const history = self.user?.history_eatery_view?.recently?.audience
    const preference = self.preferences?.audience

    userAudiencesPriorityCalced = resolveUserPriority(
        history,
        preference,
        AUDIENCES_PRIORITY_DEF
    ).filter(value => value !== 'none' && !value.startsWith('___'))

    return userAudiencesPriorityCalced
}

export function userHasPreferences(pref: EateryStats | null): boolean {
    if (!pref) return false

    const audience = Object.values(pref.audience).some(Boolean)
    const decor = Object.values(pref.decor).some(Boolean)
    const group = Object.values(pref.group).some(Boolean)
    const domain = Object.values(pref.domain).some(Boolean)
    const tags = Object.values(pref.tags).some(Boolean)

    return audience || decor || group || domain || tags
}

const EACH_DAY_MS = 60 * 60 * 1000
const DAYS = 45
const STAFF_THRESHOLD_TO_NOT_ACTIVE_EATERY = DAYS * EACH_DAY_MS

export const isEateryStaffActive = (lastActivityTime: number): boolean => {
    return Date.now() - lastActivityTime <= STAFF_THRESHOLD_TO_NOT_ACTIVE_EATERY
}

export function randomBetween(a: number, b: number): number {
    return Math.random() * (Math.max(a, b) - Math.min(a, b)) + Math.min(a, b)
}
